import { useSEO } from '../../../utils/seo';

export default function SignsBoiseFenceNeedsReplacement() {
  useSEO({
    title: 'Signs Your Boise Fence Needs Replacement | When to Replace Guide',
    description: '10 warning signs your Boise fence needs replacement. Learn when to repair vs replace, cost considerations, and how to extend fence lifespan in Idaho climate.',
    keywords: 'fence replacement Boise, when replace fence, fence repair vs replace, Idaho fence damage, fence lifespan',
    canonical: `${import.meta.env.VITE_SITE_URL}/blog/signs-boise-fence-needs-replacement`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Signs Your Boise Fence Needs Replacement: Complete Guide',
          description: '10 warning signs your Boise fence needs replacement. Learn when to repair vs replace, cost considerations, and how to extend fence lifespan.',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: `${import.meta.env.VITE_SITE_URL}/images/logo.svg`
            }
          },
          datePublished: '2025-01-15',
          dateModified: '2025-01-15',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${import.meta.env.VITE_SITE_URL}/blog/signs-boise-fence-needs-replacement`
          }
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How long do fences last in Boise Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Wood fences last 15-20 years in Boise with proper maintenance, vinyl fences last 20-30 years, and chain-link fences last 15-25 years. Idaho\'s temperature fluctuations and UV exposure affect fence lifespan significantly.'
              }
            },
            {
              '@type': 'Question',
              name: 'When should I repair vs replace my fence in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Repair if damage affects less than 20% of the fence, posts are stable, and the fence is under 10 years old. Replace if damage exceeds 30%, multiple posts are rotted, the fence leans significantly, or it\'s over 15 years old.'
              }
            },
            {
              '@type': 'Question',
              name: 'What are signs of fence rot in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Signs include soft, spongy wood when pressed, dark discoloration at ground level, crumbling wood texture, visible mold or fungus growth, and posts that wobble easily. Check fence posts first as they rot fastest.'
              }
            },
            {
              '@type': 'Question',
              name: 'How much does fence replacement cost in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Fence replacement in Boise costs $15-45 per linear foot depending on material. Wood privacy fences cost $20-35/ft, vinyl costs $30-45/ft, and chain-link costs $15-25/ft. Add $500-1,000 for old fence removal.'
              }
            },
            {
              '@type': 'Question',
              name: 'Can I replace just part of my fence?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, partial fence replacement works if the damaged section is isolated and materials match existing fence. However, if your fence is over 12 years old, replacing the entire fence ensures uniform appearance and avoids repeated repairs.'
              }
            },
            {
              '@type': 'Question',
              name: 'Does homeowners insurance cover fence replacement in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Homeowners insurance typically covers fence damage from covered perils like storms, fire, or vandalism, minus your deductible. Normal wear, rot, and age-related deterioration are not covered. Check your policy for specific coverage.'
              }
            }
          ]
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: import.meta.env.VITE_SITE_URL
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${import.meta.env.VITE_SITE_URL}/blog`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Signs Boise Fence Needs Replacement',
              item: `${import.meta.env.VITE_SITE_URL}/blog/signs-boise-fence-needs-replacement`
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-6 py-16">
        <header className="mb-12">
          <div className="text-sm text-teal-600 font-medium mb-4">FENCE INSTALLATION</div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Signs Your Boise Fence Needs Replacement: Complete Guide
          </h1>
          <div className="flex items-center gap-6 text-gray-600 text-sm">
            <time dateTime="2025-01-15">January 15, 2025</time>
            <span>9 min read</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Knowing when to replace your fence in Boise can save you money and prevent safety hazards. Idaho's harsh climate—with temperature swings from below freezing to over 100°F, intense UV exposure, and moisture from snow and rain—accelerates fence deterioration. This comprehensive guide identifies 10 warning signs your fence needs replacement, helps you decide between repair and replacement, and provides cost considerations for Treasure Valley homeowners.
          </p>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 my-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">10 Warning Signs Your Fence Needs Replacement</h2>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Rotting or Decaying Wood</h3>
                    <p className="text-gray-700 mb-3">Soft, spongy wood that crumbles when pressed indicates advanced rot. Check fence posts at ground level—this is where rot starts first in Idaho's freeze-thaw cycles.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> If more than 3-4 posts show rot, replacement is more cost-effective than repairs.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Leaning or Unstable Posts</h3>
                    <p className="text-gray-700 mb-3">Posts that lean more than 2-3 inches or wobble when pushed indicate foundation failure. This compromises the entire fence structure and creates safety hazards.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Multiple leaning posts require full replacement; isolated issues can be repaired.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Extensive Warping or Splitting</h3>
                    <p className="text-gray-700 mb-3">Boise's temperature extremes cause wood to expand and contract, leading to warped boards and large splits. If 30%+ of boards are warped, replacement is necessary.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Widespread warping indicates the fence has reached end-of-life.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">4</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Missing or Broken Boards</h3>
                    <p className="text-gray-700 mb-3">Gaps from missing boards compromise privacy and security. If you're replacing boards annually, the fence structure is failing and full replacement is more economical.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Replace if missing boards exceed 20% of total fence.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">5</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Rust and Corrosion (Metal Fences)</h3>
                    <p className="text-gray-700 mb-3">Chain-link and metal fences develop rust from Idaho's moisture. Surface rust can be treated, but extensive corrosion that weakens the structure requires replacement.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Replace if rust has created holes or significantly weakened posts.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">6</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Insect Damage</h3>
                    <p className="text-gray-700 mb-3">Termites, carpenter ants, and wood-boring beetles create tunnels and holes in fence wood. Look for small holes, sawdust piles, and hollow-sounding wood when tapped.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Widespread infestation requires replacement; treat and monitor isolated damage.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">7</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Fading and Discoloration</h3>
                    <p className="text-gray-700 mb-3">Severe fading from UV exposure indicates the protective finish has failed. While cosmetic, it signals the fence is aging and may have underlying structural issues.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Restaining can help, but if combined with other issues, consider replacement.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">8</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Loose or Failing Hardware</h3>
                    <p className="text-gray-700 mb-3">Nails popping out, screws backing out, and hinges failing indicate the wood is deteriorating and can no longer hold fasteners securely.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> If hardware fails repeatedly after repairs, the wood structure is compromised.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">9</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Age Over 15 Years</h3>
                    <p className="text-gray-700 mb-3">Most wood fences in Boise reach end-of-life at 15-20 years. Even with maintenance, structural integrity declines significantly after this point.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Plan for replacement if your fence is approaching 15 years old.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-lg">10</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Frequent Repairs Needed</h3>
                    <p className="text-gray-700 mb-3">If you're repairing your fence multiple times per year, the cumulative cost exceeds replacement value. This indicates systemic failure rather than isolated damage.</p>
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700">
                      <strong>Action Required:</strong> Replace if annual repair costs exceed 30% of replacement cost.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Repair vs Replace: Decision Guide</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Deciding between repair and replacement depends on damage extent, fence age, and long-term cost considerations. Use this guide to make an informed decision for your Boise fence.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-tools-line text-3xl text-green-600"></i>
                Choose Repair If:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Damage affects less than 20% of fence</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Fence is under 10 years old</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Posts are stable and rot-free</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Damage is isolated to one section</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Repair cost is under 30% of replacement</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Matching materials are available</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-green-200">
                <div className="font-bold text-gray-900 mb-2">Typical Repair Costs:</div>
                <div className="text-gray-700">$200-800 for isolated damage</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-refresh-line text-3xl text-red-600"></i>
                Choose Replacement If:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Damage exceeds 30% of fence</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Fence is over 15 years old</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Multiple posts are rotted or leaning</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Structural integrity is compromised</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Repairs needed multiple times yearly</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                  <span className="text-gray-700">You want to upgrade material or style</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-red-200">
                <div className="font-bold text-gray-900 mb-2">Typical Replacement Costs:</div>
                <div className="text-gray-700">$3,000-9,000 for 200 ft fence</div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Fence Replacement Cost in Boise</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding replacement costs helps you budget appropriately and compare repair vs replacement economics. Prices vary based on material, fence length, and site conditions.
          </p>

          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Boise Fence Replacement Costs (Per Linear Foot)</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-gray-700">Wood Privacy Fence (Cedar)</span>
                <strong className="text-teal-600">$25-35/ft</strong>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-gray-700">Wood Privacy Fence (Pine)</span>
                <strong className="text-teal-600">$20-30/ft</strong>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-gray-700">Vinyl Privacy Fence</span>
                <strong className="text-teal-600">$30-45/ft</strong>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-gray-700">Chain-Link Fence</span>
                <strong className="text-teal-600">$15-25/ft</strong>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-gray-700">Old Fence Removal</span>
                <strong className="text-amber-600">$500-1,000</strong>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Disposal Fees</span>
                <strong className="text-amber-600">$200-400</strong>
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Total Project Cost Examples</h3>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-teal-50 rounded-xl p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-3">Small Yard (100 ft)</h4>
              <div className="space-y-2 text-gray-700 text-sm mb-4">
                <div className="flex justify-between">
                  <span>Cedar fence:</span>
                  <strong>$2,500-3,500</strong>
                </div>
                <div className="flex justify-between">
                  <span>Vinyl fence:</span>
                  <strong>$3,000-4,500</strong>
                </div>
                <div className="flex justify-between">
                  <span>Removal:</span>
                  <strong>$300-500</strong>
                </div>
              </div>
              <div className="pt-3 border-t border-teal-200">
                <div className="font-bold text-teal-600">Total: $2,800-5,000</div>
              </div>
            </div>

            <div className="bg-teal-50 rounded-xl p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-3">Medium Yard (200 ft)</h4>
              <div className="space-y-2 text-gray-700 text-sm mb-4">
                <div className="flex justify-between">
                  <span>Cedar fence:</span>
                  <strong>$5,000-7,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Vinyl fence:</span>
                  <strong>$6,000-9,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Removal:</span>
                  <strong>$600-800</strong>
                </div>
              </div>
              <div className="pt-3 border-t border-teal-200">
                <div className="font-bold text-teal-600">Total: $5,600-9,800</div>
              </div>
            </div>

            <div className="bg-teal-50 rounded-xl p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-3">Large Yard (300 ft)</h4>
              <div className="space-y-2 text-gray-700 text-sm mb-4">
                <div className="flex justify-between">
                  <span>Cedar fence:</span>
                  <strong>$7,500-10,500</strong>
                </div>
                <div className="flex justify-between">
                  <span>Vinyl fence:</span>
                  <strong>$9,000-13,500</strong>
                </div>
                <div className="flex justify-between">
                  <span>Removal:</span>
                  <strong>$900-1,200</strong>
                </div>
              </div>
              <div className="pt-3 border-t border-teal-200">
                <div className="font-bold text-teal-600">Total: $8,400-14,700</div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Extend Your Fence Lifespan</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Proper maintenance significantly extends fence life in Idaho's harsh climate. Follow these practices to maximize your fence investment.
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
                <i className="ri-paint-brush-line text-2xl text-teal-600"></i>
                Stain or Seal Every 2-3 Years
              </h3>
              <p className="text-gray-700">
                Protective coatings prevent moisture absorption and UV damage. Boise's intense sun and temperature swings make regular sealing essential for wood fences.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
                <i className="ri-search-eye-line text-2xl text-blue-600"></i>
                Annual Inspections
              </h3>
              <p className="text-gray-700">
                Check for rot, loose boards, and hardware issues each spring. Early detection prevents minor problems from becoming major repairs.
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
                <i className="ri-water-flash-line text-2xl text-green-600"></i>
                Improve Drainage
              </h3>
              <p className="text-gray-700">
                Ensure water drains away from fence posts. Standing water accelerates rot. Grade soil away from posts and clear debris that traps moisture.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
                <i className="ri-scissors-cut-line text-2xl text-purple-600"></i>
                Trim Vegetation
              </h3>
              <p className="text-gray-700">
                Keep plants, vines, and grass away from fence boards. Vegetation traps moisture against wood, promoting rot and insect damage.
              </p>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
                <i className="ri-hammer-line text-2xl text-amber-600"></i>
                Repair Damage Promptly
              </h3>
              <p className="text-gray-700">
                Fix loose boards, replace damaged sections, and tighten hardware immediately. Small repairs prevent cascading failures.
              </p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6 mb-12">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How long do fences last in Boise Idaho?</h3>
              <p className="text-gray-700 leading-relaxed">
                Wood fences last 15-20 years in Boise with proper maintenance, vinyl fences last 20-30 years, and chain-link fences last 15-25 years. Idaho's temperature fluctuations and UV exposure affect fence lifespan significantly.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">When should I repair vs replace my fence in Boise?</h3>
              <p className="text-gray-700 leading-relaxed">
                Repair if damage affects less than 20% of the fence, posts are stable, and the fence is under 10 years old. Replace if damage exceeds 30%, multiple posts are rotted, the fence leans significantly, or it's over 15 years old.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What are signs of fence rot in Idaho?</h3>
              <p className="text-gray-700 leading-relaxed">
                Signs include soft, spongy wood when pressed, dark discoloration at ground level, crumbling wood texture, visible mold or fungus growth, and posts that wobble easily. Check fence posts first as they rot fastest.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How much does fence replacement cost in Boise?</h3>
              <p className="text-gray-700 leading-relaxed">
                Fence replacement in Boise costs $15-45 per linear foot depending on material. Wood privacy fences cost $20-35/ft, vinyl costs $30-45/ft, and chain-link costs $15-25/ft. Add $500-1,000 for old fence removal.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Can I replace just part of my fence?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, partial fence replacement works if the damaged section is isolated and materials match existing fence. However, if your fence is over 12 years old, replacing the entire fence ensures uniform appearance and avoids repeated repairs.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Does homeowners insurance cover fence replacement in Idaho?</h3>
              <p className="text-gray-700 leading-relaxed">
                Homeowners insurance typically covers fence damage from covered perils like storms, fire, or vandalism, minus your deductible. Normal wear, rot, and age-related deterioration are not covered. Check your policy for specific coverage.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-600 to-cyan-600 rounded-2xl p-8 text-white text-center my-12">
            <h2 className="text-3xl font-bold mb-4">Need a Fence Inspection or Replacement?</h2>
            <p className="text-xl mb-6 text-teal-50">
              Get a professional assessment of your Boise fence condition. We'll provide honest recommendations on repair vs replacement and a detailed quote.
            </p>
            <a 
              href="/#contact" 
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule Free Fence Inspection
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <a href="/blog/fence-installation-cost-boise-idaho" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-article-line text-2xl text-teal-600"></i>
                <span className="text-gray-900 font-medium">Fence Installation Cost in Boise Idaho</span>
              </a>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}